import { Check, Users, User, UsersRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const Services = ({ services, onPaymentSelect }) => {
  const serviceIcons = {
    "one-on-one": User,
    "cohort": UsersRound,
    "group": Users,
  };

  const serviceImages = {
    "one-on-one": "https://images.unsplash.com/photo-1714976694810-85add1a29c96?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400",
    "cohort": "https://images.unsplash.com/photo-1565350897149-38dfafa81d83?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400",
    "group": "https://images.unsplash.com/photo-1672917187338-7f81ecac3d3f?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=400",
  };

  const defaultServices = [
    {
      id: "one-on-one",
      title: "1-on-1 Leadership Coaching",
      description: "Personalized guidance to unlock your leadership potential with focused, one-on-one sessions tailored to your specific goals and challenges.",
      features: [
        "60-minute deep dive sessions",
        "Personality and leadership assessment",
        "Customized action plan",
        "Email support between sessions"
      ],
      pricing: [
        { name: "Single Session", price: 200, description: "One 60-minute coaching session" },
        { name: "3-Session Package", price: 500, description: "Three sessions (Save $100)", popular: true }
      ]
    },
    {
      id: "cohort",
      title: "Cohort-Based Group Coaching",
      description: "Learn and grow alongside peers in a structured group program. Perfect for teams or professionals seeking community-driven growth.",
      features: [
        "3-6 participants per cohort",
        "Topic-focused curriculum",
        "Peer accountability partners",
        "Group exercises and discussions"
      ],
      pricing: [
        { name: "Single Session", price: 100, description: "Per person, per session" },
        { name: "3-Session Bundle", price: 200, description: "Per person (Save $100)", popular: true }
      ]
    },
    {
      id: "group",
      title: "Group Coaching Sessions",
      description: "Dynamic group sessions for teams looking to develop leadership skills together. Ideal for corporate teams and organizations.",
      features: [
        "3-6 participants per session",
        "Team dynamics focus",
        "Interactive workshops",
        "Collaborative problem-solving"
      ],
      pricing: [
        { name: "Single Session", price: 100, description: "Per person, per session" },
        { name: "3-Session Bundle", price: 200, description: "Per person (Save $100)", popular: true }
      ]
    }
  ];

  const displayServices = services?.length > 0 ? services : defaultServices;

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
            Services
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Coaching Programs
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Choose the coaching format that best fits your needs. From personalized 
            one-on-one sessions to dynamic group programs.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {displayServices.map((service, index) => {
            const Icon = serviceIcons[service.id] || User;
            return (
              <Card
                key={service.id}
                data-testid={`service-card-${service.id}`}
                className="service-card border-border/50 bg-background overflow-hidden"
              >
                {/* Service Image */}
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={serviceImages[service.id] || serviceImages["one-on-one"]}
                    alt={service.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="p-2 bg-background/90 backdrop-blur-sm rounded-lg">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                </div>
                
                <CardHeader className="pb-4">
                  <CardTitle className="text-xl">{service.title}</CardTitle>
                  <CardDescription className="text-sm leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  {/* Features */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold">What's Included:</h4>
                    <ul className="space-y-2">
                      {service.features.map((feature, fIndex) => (
                        <li
                          key={fIndex}
                          className="flex items-start gap-2 text-sm text-muted-foreground"
                        >
                          <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Examples (if available) */}
                  {service.examples && service.examples.length > 0 && (
                    <div className="space-y-3">
                      <h4 className="text-sm font-semibold">Real-World Examples:</h4>
                      <ul className="space-y-2">
                        {service.examples.map((example, eIndex) => (
                          <li
                            key={eIndex}
                            className="text-xs text-muted-foreground pl-3 border-l-2 border-primary/30"
                          >
                            {example}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Book Discovery Call Button */}
                  <div className="pt-4 border-t border-border/50">
                    <Button
                      data-testid={`book-btn-${service.id}`}
                      onClick={() => {
                        const element = document.querySelector("#booking");
                        if (element) element.scrollIntoView({ behavior: "smooth" });
                      }}
                      className="w-full bg-primary text-white hover:bg-primary/90"
                    >
                      Book a Discovery Call
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Not sure which program is right for you?
          </p>
          <Button
            data-testid="services-cta-btn"
            onClick={() => {
              const element = document.querySelector("#booking");
              if (element) element.scrollIntoView({ behavior: "smooth" });
            }}
            size="lg"
            className="btn-primary bg-primary text-primary-foreground"
          >
            Book a Free Discovery Call
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Services;
